import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  XMarkIcon,
  PaperClipIcon,
  PaperAirplaneIcon,
  TrashIcon,
  CodeBracketIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { emailDraftsApi } from '../../api/emailApi';

// Simple debounce implementation
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};

const DraftCompose = ({
  show,
  onClose,
  onSend,
  replyTo,
  sending,
  selectedAccount,
  draftId,
  onDraftSaved,
  onDraftDeleted,
}) => {
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [isHtml, setIsHtml] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(draftId);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Load existing draft if editing
  useEffect(() => {
    if (draftId && selectedAccount) {
      loadDraft(draftId);
    }
  }, [draftId, selectedAccount]);

  // Initialize reply data
  useEffect(() => {
    if (replyTo && !draftId) {
      setTo(replyTo.fromEmail ? [replyTo.fromEmail] : []);
      setSubject(`Re: ${replyTo.subject?.replace(/^Re:\s*/i, '') || ''}`);
      setBody(
        `\n\n---\nOn ${new Date(replyTo.date).toLocaleString()}, ${replyTo.from || replyTo.fromEmail} wrote:\n\n${replyTo.bodyText || ''}`
      );
    }
  }, [replyTo, draftId]);

  const loadDraft = async (id) => {
    try {
      const draft = await emailDraftsApi.getById(selectedAccount.id, id);
      setTo(draft.to || []);
      setCc(draft.cc || []);
      setSubject(draft.subject || '');
      setBody(draft.body || '');
      setIsHtml(draft.isHtml || false);
      setCurrentDraftId(draft.id);
    } catch (error) {
      console.error('Failed to load draft:', error);
    }
  };

  // Auto-save draft function
  const saveDraftInternal = useCallback(async (draftData) => {
    if (!selectedAccount) return;

    setSaving(true);
    try {
      const draft = {
        to: draftData.to,
        cc: draftData.cc.length > 0 ? draftData.cc : null,
        subject: draftData.subject,
        body: draftData.body,
        isHtml: draftData.isHtml,
        replyToUid: replyTo?.uid?.toString(),
        replyToFolder: replyTo?.folder,
      };

      let savedDraft;
      if (currentDraftId) {
        savedDraft = await emailDraftsApi.update(selectedAccount.id, currentDraftId, draft);
      } else {
        savedDraft = await emailDraftsApi.create(selectedAccount.id, draft);
        setCurrentDraftId(savedDraft.id);
      }

      setLastSaved(new Date());
      onDraftSaved?.(savedDraft);
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      setSaving(false);
    }
  }, [selectedAccount, currentDraftId, replyTo, onDraftSaved]);

  // Debounced save function
  const saveDraft = useDebounce(saveDraftInternal, 2000);

  // Trigger auto-save when content changes
  useEffect(() => {
    if (to.length > 0 || subject || body) {
      saveDraft({ to, cc, subject, body, isHtml });
    }
  }, [to, cc, subject, body, isHtml, saveDraft]);

  const handleAddRecipient = (type, email) => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) return;

    if (type === 'to' && !to.includes(trimmed)) {
      setTo([...to, trimmed]);
      setToInput('');
    } else if (type === 'cc' && !cc.includes(trimmed)) {
      setCc([...cc, trimmed]);
      setCcInput('');
    }
  };

  const handleRemoveRecipient = (type, email) => {
    if (type === 'to') {
      setTo(to.filter((e) => e !== email));
    } else {
      setCc(cc.filter((e) => e !== email));
    }
  };

  const handleKeyDown = (e, type) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddRecipient(type, type === 'to' ? toInput : ccInput);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
    e.target.value = '';
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (to.length === 0) {
      alert('Please add at least one recipient');
      return;
    }

    // Delete draft if it was saved
    if (currentDraftId && selectedAccount) {
      try {
        await emailDraftsApi.delete(selectedAccount.id, currentDraftId);
        onDraftDeleted?.(currentDraftId);
      } catch (error) {
        console.error('Failed to delete draft after send:', error);
      }
    }

    onSend({
      to,
      cc: cc.length > 0 ? cc : null,
      subject,
      body,
      isHtml,
      attachments,
    });
  };

  const handleDeleteDraft = async () => {
    if (currentDraftId && selectedAccount) {
      try {
        await emailDraftsApi.delete(selectedAccount.id, currentDraftId);
        onDraftDeleted?.(currentDraftId);
      } catch (error) {
        console.error('Failed to delete draft:', error);
      }
    }
    handleClose();
  };

  const handleClose = () => {
    setTo([]);
    setCc([]);
    setSubject('');
    setBody('');
    setAttachments([]);
    setToInput('');
    setCcInput('');
    setIsHtml(false);
    setCurrentDraftId(null);
    setLastSaved(null);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto max-w-3xl shadow-lg rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-100 rounded-t-lg border-b">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900">
              {replyTo ? 'Reply' : currentDraftId ? 'Edit Draft' : 'New Email'}
            </h3>
            {saving && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <DocumentTextIcon className="h-3 w-3 animate-pulse" />
                Saving...
              </span>
            )}
            {lastSaved && !saving && (
              <span className="text-xs text-gray-500">
                Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-gray-200 text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4">
          {/* From (display only) */}
          {selectedAccount && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 w-12">From:</span>
              <span className="text-gray-900">{selectedAccount.emailAddress}</span>
            </div>
          )}

          {/* To */}
          <div className="flex items-start gap-2">
            <span className="text-gray-500 text-sm w-12 pt-2">To:</span>
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                {to.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {email}
                    <button
                      onClick={() => handleRemoveRecipient('to', email)}
                      className="hover:text-blue-600"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={toInput}
                  onChange={(e) => setToInput(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, 'to')}
                  onBlur={() => handleAddRecipient('to', toInput)}
                  placeholder={to.length === 0 ? 'Add recipients...' : ''}
                  className="flex-1 min-w-[150px] outline-none text-sm"
                />
              </div>
            </div>
          </div>

          {/* CC */}
          <div className="flex items-start gap-2">
            <span className="text-gray-500 text-sm w-12 pt-2">CC:</span>
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                {cc.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                  >
                    {email}
                    <button
                      onClick={() => handleRemoveRecipient('cc', email)}
                      className="hover:text-gray-600"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, 'cc')}
                  onBlur={() => handleAddRecipient('cc', ccInput)}
                  placeholder={cc.length === 0 ? 'Add CC recipients...' : ''}
                  className="flex-1 min-w-[150px] outline-none text-sm"
                />
              </div>
            </div>
          </div>

          {/* Subject */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm w-12">Subject:</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          {/* HTML Mode Toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsHtml(!isHtml)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isHtml
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              <CodeBracketIcon className="h-4 w-4" />
              {isHtml ? 'HTML Mode' : 'Plain Text'}
            </button>
            {isHtml && (
              <span className="text-xs text-gray-500">
                You can write raw HTML in the body
              </span>
            )}
          </div>

          {/* Body */}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              isHtml
                ? 'Write your HTML content...\n\n<html>\n  <body>\n    <h1>Hello!</h1>\n  </body>\n</html>'
                : 'Write your message...'
            }
            rows={12}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none ${
              isHtml ? 'font-mono bg-gray-50' : ''
            }`}
          />

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <PaperClipIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Attachments</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
                  >
                    <span className="text-sm text-gray-700 truncate max-w-[200px]">
                      {file.name}
                    </span>
                    <button
                      onClick={() => handleRemoveAttachment(index)}
                      className="text-gray-500 hover:text-red-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-b-lg border-t">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer">
              <PaperClipIcon className="h-5 w-5" />
              Attach file
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            {currentDraftId && (
              <button
                onClick={handleDeleteDraft}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
              >
                <TrashIcon className="h-4 w-4" />
                Delete Draft
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || to.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="h-4 w-4" />
                  Send
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraftCompose;



